import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, useMutation} from 'react-relay/hooks';
import classNames from 'classnames';
import {useLocation} from 'react-router-dom';
import DotsVerticalIcon from '../../../../assets/icons0/dots-vertical.svg';
import Link01Icon from '../../../../assets/icons0/link-01.svg';
import LinkBroken01Icon from '../../../../assets/icons0/link-broken-01.svg';
import Trash01Icon from '../../../../assets/icons0/trash-01.svg';
import LinkExternal01Icon from '../../../../assets/icons0/link-external-01.svg';
import {getRecaptchaToken} from '../../../../utils/recaptcha';
import {invalidateUserConnections, invalidateUserFollowing, invalidateFollowingPosts, invalidateForMePosts} from '../../../../utilities/invalidate-vibe-user-connections';
import {truncateVibeUsername} from '../../utilities/truncate-vibe-username';
import searchFilter from '../../../../searchFilter';
import handlePostLoginAndFollowRedirect from '../../utilities/handle-post-login-and-follow-redirect';

const MenuRowContent = React.memo(({Icon, children}) => {
    return (
        <div className='display-flex align-items-center column-gap-0dot5rem'>
            {Icon && <Icon className='display-block width-1dot25rem height-1dot25rem color-gray-400' />}
            {children}
        </div>
    );
});

const PostMenu = React.memo(({
    postId,
    postAuthorId,
    currentUserId,
    isAuthorized,
    isFollowing,
    postAuthorUsername,
}) => {
    const location = useLocation();
    const [dropDownVisible, setDropDownVisibility] = React.useState(false);
    const menuButtonRef = React.useRef();

    React.useEffect(() => {
        const onClickOutsideHandler = (event) => {
            if (dropDownVisible && menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
                setDropDownVisibility(false);
            }
        };
        document.addEventListener('click', onClickOutsideHandler, true);
        return () => {
            document.removeEventListener('click', onClickOutsideHandler, true);
        };
    }, [dropDownVisible]);

    const [followUser, isFollowingUser] = useMutation(
        graphql`
            mutation postMenuFollowUserMutation($userId: String!) {
                followUser(userId: $userId) {
                    id
                    follower {
                        id
                        followingCount
                    }
                    following {
                        id
                        followersCount
                        followingUser
                    }
                }
            }
        `
    );

    const [unfollowUser, isUnfollowingUser] = useMutation(
        graphql`
            mutation postMenuUnfollowUserMutation($userId: String!) {
                unfollowUser(userId: $userId) {
                    id
                    follower {
                        id
                        followingCount
                    }
                    following {
                        id
                        followersCount
                        followingUser
                    }
                }
            }
        `
    );

    const [removePost, isRemovingPost] = useMutation(
        graphql`
            mutation postMenuRemovePostMutation($request: RemoveVibePostRequestInput!) {
                removeVibePost(request: $request) {
                    id
                    removed
                    sharedPost {
                        id
                        sharesCount
                        sharedByCurrentUser
                    }
                    quotedPost {
                        id
                        quotesCount
                        quotedByCurrentUser
                    }
                    parent {
                        id
                        commentsCount
                        commentedByCurrentUser
                    }
                }
            }
        `
    );

    const handleFollowClick = () => {
        if (!isAuthorized) {
            handlePostLoginAndFollowRedirect(postId, postAuthorId);
            return;
        }

        followUser({
            variables: {userId: postAuthorId},
            updater: (store) => {
                invalidateUserConnections(store, postAuthorId);
                invalidateUserFollowing(store, currentUserId);
                invalidateFollowingPosts(store);
                invalidateForMePosts(store);
            },
            onCompleted: () => {
                setDropDownVisibility(false);
            }
        });
    };

    const handleUnfollowClick = () => {
        if (!isAuthorized) {
            handlePostLoginRedirect(postId);
            return;
        }

        unfollowUser({
            variables: {userId: postAuthorId},
            updater: (store) => {
                invalidateUserConnections(store, postAuthorId);
                invalidateUserFollowing(store, currentUserId);
                invalidateFollowingPosts(store);
                invalidateForMePosts(store);
            },
            onCompleted: () => {
                setDropDownVisibility(false);
            }
        });
    };

    const handleRemoveClick = async () => {
        try {
            const reCaptcha = await getRecaptchaToken();
            removePost({
                variables: {request: {id: postId, reCaptcha}},
                onCompleted: () => {
                    setDropDownVisibility(false);
                }
            });
        } catch (error) {
            console.error('reCAPTCHA error:', error);
        }
    };

    const isOwnPost = currentUserId && currentUserId === postAuthorId;

    const showFollow = !isFollowing && !isOwnPost;
    const showUnfollow = isFollowing && !isOwnPost;

    const showRemove = isOwnPost;
    const showOpenInNewTab = true;
    const hasOptions = showOpenInNewTab || showFollow || showUnfollow || showRemove;

    if (!hasOptions) {
        return null;
    }

    const username = postAuthorUsername || postAuthorId;
    const usernameShort = truncateVibeUsername(username);
    const followLoading = isFollowingUser;
    const unfollowLoading = isUnfollowingUser;
    const postLink = `/vibe/posts/${postId}${searchFilter(location.search)}`;

    return (
        <div className='position-relative' ref={menuButtonRef}>
            <DotsVerticalIcon
                className='display-block color-gray-400 height-1dot25rem width-1dot25rem cursor-pointer'
                onClick={() => setDropDownVisibility(!dropDownVisible)}
            />
            <div
                className={classNames(
                    'shadow-lg position-absolute width-max-content right-0 top-1dot75rem background-color-white border-radius-0dot5rem padding-0dot5rem display-flex flex-direction-column row-gap-0dot25rem',
                    {'display-block': dropDownVisible, 'display-none': !dropDownVisible}
                )}
                style={{zIndex: 10}}
            >
                {showOpenInNewTab && (
                    <a
                        href={postLink}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-decoration-none background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem display-flex align-items-center column-gap-0dot5rem width-100percent hover-background-color-gray-100 cursor-pointer'
                        onClick={() => setDropDownVisibility(false)}
                    >
                        <MenuRowContent Icon={LinkExternal01Icon}>
                            <span className='text-sm color-gray-500'>
                                <FormattedMessage defaultMessage='Open post in new tab' />
                            </span>
                        </MenuRowContent>
                    </a>
                )}

                {showFollow && (
                    <button
                        type='button'
                        disabled={followLoading}
                        className={classNames('background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem display-flex align-items-center column-gap-0dot5rem width-100percent', {
                            'opacity-0dot5 cursor-not-allowed': followLoading,
                            'hover-background-color-gray-100 cursor-pointer': !followLoading
                        })}
                        onClick={handleFollowClick}
                    >
                        <MenuRowContent Icon={Link01Icon}>
                            <div className='text-sm color-gray-500'>
                                <FormattedMessage defaultMessage='Follow' /> @{usernameShort}
                                {followLoading ? '...' : ''}
                            </div>
                        </MenuRowContent>
                    </button>
                )}

                {showUnfollow && (
                    <button
                        type='button'
                        disabled={unfollowLoading}
                        className={classNames('background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem display-flex align-items-center column-gap-0dot5rem width-100percent', {
                            'opacity-0dot5 cursor-not-allowed': unfollowLoading,
                            'hover-background-color-gray-100 cursor-pointer': !unfollowLoading
                        })}
                        onClick={handleUnfollowClick}
                    >
                        <MenuRowContent Icon={LinkBroken01Icon}>
                            <div className='text-sm color-gray-500'>
                                <FormattedMessage defaultMessage='Unfollow' /> @{usernameShort}
                                {unfollowLoading ? '...' : ''}
                            </div>
                        </MenuRowContent>
                    </button>
                )}

                {showRemove && (
                    <button
                        type='button'
                        disabled={isRemovingPost}
                        className={classNames('background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem width-100percent text-align-left', {
                            'opacity-0dot5 cursor-not-allowed': isRemovingPost,
                            'hover-background-color-gray-100 cursor-pointer': !isRemovingPost
                        })}
                        onClick={handleRemoveClick}
                    >
                        <MenuRowContent Icon={Trash01Icon}>
                            <span className='text-sm color-gray-500'>
                                <FormattedMessage defaultMessage='Remove post' />
                                {isRemovingPost ? '...' : ''}
                            </span>
                        </MenuRowContent>
                    </button>
                )}
            </div>
        </div>
    );
});

export default PostMenu;
